// Обзорът на периода: шест числа и лентата с активността.
//
// Числата идват от `GET /audit-logs/stats` и са за ЦЕЛИЯ период по активните
// филтри, не за заредената страница. Трите, зад които стои филтър (проблеми,
// пари, настройки), са бутони — кутийката отговаря на въпроса и веднага
// стеснява списъка до него.

import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { AuditLogStats } from "../../../../api/auditLogs";

/** Повече деления от това не се четат — лентата спира да ги допълва. */
const MAX_BUCKETS = 480;

const formatCount = (value: number) => value.toLocaleString("bg-BG");

const formatDuration = (ms: number) =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;

type TileProps = {
  kicker: string;
  value: string;
  sub?: string;
  tone?: "ink" | "danger" | "brand";
  active?: boolean;
  onClick?: () => void;
};

const Tile = ({ kicker, value, sub, tone = "ink", active, onClick }: TileProps) => {
  const valueClass =
    tone === "danger"
      ? "text-danger-ink"
      : tone === "brand"
        ? "text-brand-deep-ink"
        : "text-ink";
  const body = (
    <>
      <p className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {kicker}
      </p>
      <p
        className={`mb-1 mt-2 font-pos-mono text-[22px] font-extrabold leading-none tracking-[-0.02em] tabular-nums ${valueClass}`}
      >
        {value}
      </p>
      <span className="block min-h-4 text-[11.5px] font-medium leading-[1.2] text-muted">
        {sub ?? ""}
      </span>
    </>
  );
  const shell = `flex min-w-0 flex-col items-start rounded-xl border px-3.5 py-3 text-left ${
    active ? "border-brand-tint2 bg-brand-tint" : "border-shade-2 bg-surface"
  }`;
  if (!onClick) return <article className={shell}>{body}</article>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${shell} transition-colors hover:border-brand-tint2 hover:bg-brand-tint`}
    >
      {body}
    </button>
  );
};

type Props = {
  stats?: AuditLogStats;
  loading: boolean;
  isNarrow: boolean;
  activeProblems: boolean;
  activeMoney: boolean;
  activeSettings: boolean;
  onToggleProblems: () => void;
  onToggleMoney: () => void;
  onToggleSettings: () => void;
  /** Клик по деление на лентата стеснява периода до самото него. */
  onPickPeriod: (range: [Dayjs, Dayjs]) => void;
};

export const AuditOverview = ({
  stats,
  loading,
  isNarrow,
  activeProblems,
  activeMoney,
  activeSettings,
  onToggleProblems,
  onToggleMoney,
  onToggleSettings,
  onPickPeriod,
}: Props) => {
  const { t } = useTranslation();

  // BE-то връща само деленията, в които има записи. За да се чете „тук е
  // тихо, а тук е било напрегнато", празните между първото и последното се
  // допълват тук — иначе три записа през цял ден изглеждат като три съседни.
  const bars = useMemo(() => {
    const buckets = stats?.buckets ?? [];
    if (!buckets.length) return [];
    const unit = stats?.bucket === "day" ? "day" : "hour";
    const byStart = new Map(
      buckets.map((bucket) => [dayjs(bucket.start).valueOf(), bucket]),
    );
    const first = dayjs(buckets[0].start);
    const last = dayjs(buckets[buckets.length - 1].start);
    const span = last.diff(first, unit);
    if (span < 0 || span > MAX_BUCKETS) {
      return buckets.map((bucket) => ({ ...bucket, start: dayjs(bucket.start) }));
    }
    const filled = [];
    for (let index = 0; index <= span; index += 1) {
      const start = first.add(index, unit);
      const bucket = byStart.get(start.valueOf());
      filled.push({
        start,
        count: bucket?.count ?? 0,
        problems: bucket?.problems ?? 0,
      });
    }
    return filled;
  }, [stats?.buckets, stats?.bucket]);

  const maxCount = bars.reduce((max, bar) => Math.max(max, bar.count), 0);
  const unit = stats?.bucket === "day" ? "day" : "hour";
  const barFormat = unit === "day" ? "DD.MM.YYYY" : "DD.MM HH:mm";

  return (
    <section className="flex flex-col gap-3 rounded-[14px] border border-shade-2 bg-surface p-4">
      <div
        className={`grid gap-2.5 ${
          isNarrow ? "grid-cols-2" : "grid-cols-3 xl:grid-cols-6"
        }`}
      >
        <Tile
          kicker={t("auditLog.stats.actions")}
          value={loading && !stats ? "…" : formatCount(stats?.total ?? 0)}
          sub={t("auditLog.stats.actionsSub", {
            writes: formatCount(stats?.writes ?? 0),
            reads: formatCount(stats?.reads ?? 0),
          })}
        />
        <Tile
          kicker={t("auditLog.stats.problems")}
          value={loading && !stats ? "…" : formatCount(stats?.problems ?? 0)}
          tone={(stats?.problems ?? 0) > 0 ? "danger" : "ink"}
          sub={
            (stats?.fiscal_failed ?? 0) > 0
              ? t(
                  stats?.fiscal_failed === 1
                    ? "auditLog.stats.fiscalFailedSubOne"
                    : "auditLog.stats.fiscalFailedSub",
                  { count: stats?.fiscal_failed ?? 0 },
                )
              : t("auditLog.stats.problemsSub")
          }
          active={activeProblems}
          onClick={onToggleProblems}
        />
        <Tile
          kicker={t("auditLog.stats.money")}
          value={loading && !stats ? "…" : formatCount(stats?.money ?? 0)}
          tone="brand"
          sub={t("auditLog.stats.moneySub")}
          active={activeMoney}
          onClick={onToggleMoney}
        />
        <Tile
          kicker={t("auditLog.stats.settings")}
          value={loading && !stats ? "…" : formatCount(stats?.settings ?? 0)}
          sub={t("auditLog.stats.settingsSub")}
          active={activeSettings}
          onClick={onToggleSettings}
        />
        <Tile
          kicker={t("auditLog.stats.people")}
          value={loading && !stats ? "…" : formatCount(stats?.users ?? 0)}
          sub={
            stats?.last_at
              ? t("auditLog.stats.lastAt", {
                  when: dayjs(stats.last_at).format("DD.MM HH:mm"),
                })
              : ""
          }
        />
        <Tile
          kicker={t("auditLog.stats.slowest")}
          value={loading && !stats ? "…" : formatDuration(stats?.slowest_ms ?? 0)}
          sub={t("auditLog.stats.avg", {
            value: formatDuration(stats?.avg_ms ?? 0),
          })}
        />
      </div>

      {bars.length > 1 ? (
        <div className="flex flex-col gap-1.5 border-t border-shade-2 pt-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
              {unit === "day"
                ? t("auditLog.stats.byDay")
                : t("auditLog.stats.byHour")}
            </span>
            <span className="text-[11px] font-medium text-muted">
              {t("auditLog.stats.barsHint")}
            </span>
          </div>
          <div className="flex h-[54px] items-end gap-[2px] overflow-hidden border-b border-shade-2">
            {bars.map((bar) => {
              const share = maxCount > 0 ? (bar.count / maxCount) * 100 : 0;
              const problemShare =
                bar.count > 0 ? (bar.problems / bar.count) * 100 : 0;
              return (
                <button
                  key={bar.start.valueOf()}
                  type="button"
                  onClick={() =>
                    onPickPeriod([
                      bar.start.startOf(unit),
                      bar.start.endOf(unit),
                    ])
                  }
                  title={t("auditLog.stats.barTitle", {
                    when: bar.start.format(barFormat),
                    count: bar.count,
                    problems: bar.problems,
                  })}
                  className="group flex h-full min-w-[3px] max-w-[56px] flex-1 flex-col justify-end rounded-t-[2px]"
                >
                  {/* Височината е дял от максимума — известна чак по време на
                      изпълнение, единственият позволен inline style тук. */}
                  <span
                    className="flex w-full flex-col-reverse overflow-hidden rounded-t-[2px] bg-fill-2 transition-colors group-hover:bg-fill-3"
                    style={{ height: `${Math.max(share, bar.count ? 6 : 2)}%` }}
                  >
                    <span
                      className="w-full bg-brand-tint2 transition-colors group-hover:bg-brand"
                      style={{ height: `${100 - problemShare}%` }}
                    />
                    <span
                      className="w-full bg-danger"
                      style={{ height: `${problemShare}%` }}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};
